import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Search, Filter, X } from 'lucide-react';
import { contractCategories } from '../../data/contracts';
import { useContracts } from '../../context/ContractContext';

const ContractCatalog: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialCategory = queryParams.get('category') || '';
  
  const { contracts } = useContracts();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000]);
  
  // Filter contracts based on search term, category, and price range
  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = 
      contract.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory ? contract.category === selectedCategory : true;
    
    const matchesPrice = 
      contract.price.max >= priceRange[0] && contract.price.min <= priceRange[1];
    
    return matchesSearch && matchesCategory && matchesPrice;
  });
  
  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId === selectedCategory ? '' : categoryId);
  };
  
  const handlePriceRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (e.target.name === 'min') {
      setPriceRange([value, priceRange[1]]);
    } else {
      setPriceRange([priceRange[0], value]);
    }
  };
  
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setPriceRange([0, 1000]);
  };
  
  return (
    <div className="container mx-auto px-4 py-8 mt-8">
      <h1 className="text-3xl font-bold mb-8">Contractcatalogus</h1>
      
      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-center mb-6">
          <div className="relative flex-grow mb-4 md:mb-0 md:mr-4">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Zoek een contract..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <button
            onClick={clearFilters}
            className="flex items-center text-blue-600 hover:text-blue-800"
          >
            <X className="h-4 w-4 mr-1" />
            Filters wissen
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h3 className="font-semibold mb-3 flex items-center">
              <Filter className="h-4 w-4 mr-2" />
              Categorieën
            </h3>
            <div className="space-y-2">
              {contractCategories.map((category) => (
                <div key={category.id} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`category-${category.id}`}
                    checked={selectedCategory === category.id}
                    onChange={() => handleCategoryChange(category.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor={`category-${category.id}`}
                    className="ml-2 text-gray-700"
                  >
                    {category.name}
                  </label>
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <h3 className="font-semibold mb-3">Minimumprijs (€)</h3>
            <input
              type="range"
              name="min"
              min="0"
              max="1000"
              step="25"
              value={priceRange[0]}
              onChange={handlePriceRangeChange}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <span className="text-gray-700">{priceRange[0]} €</span>
          </div>
          
          <div>
            <h3 className="font-semibold mb-3">Maximumprijs (€)</h3>
            <input
              type="range"
              name="max"
              min="0"
              max="1000"
              step="25"
              value={priceRange[1]}
              onChange={handlePriceRangeChange}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <span className="text-gray-700">{priceRange[1]} €</span>
          </div>
        </div>
      </div>
      
      {/* Results */}
      <div className="mb-4">
        <p className="text-gray-600">
          {filteredContracts.length} contract{filteredContracts.length !== 1 ? 'en' : ''} gevonden
        </p>
      </div>
      
      {filteredContracts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredContracts.map((contract) => {
            const category = contractCategories.find(c => c.id === contract.category);
            
            return (
              <div key={contract.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h2 className="text-xl font-semibold text-gray-800">{contract.name}</h2>
                    <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                      {category?.name}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {contract.description}
                  </p>
                  
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-2xl font-bold text-gray-800">
                      {contract.price.min === contract.price.max 
                        ? `${contract.price.min} €`
                        : `${contract.price.min} - ${contract.price.max} €`
                      }
                    </span>
                    <span className="text-sm text-gray-500">{contract.processingTime}</span>
                  </div>
                  
                  <Link
                    to={`/contracts/${contract.id}`}
                    className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-300"
                  >
                    Bekijk details
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <p className="text-gray-600 mb-4">Geen contracten gevonden die aan uw zoekcriteria voldoen.</p>
          <button
            onClick={clearFilters}
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Filters resetten
          </button>
        </div>
      )}
    </div>
  );
};

export default ContractCatalog;